import React, { useState, useEffect, useCallback } from 'react';
import { Tab, Tabs, Tooltip } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import {
  FiDollarSign,
  FiInfo,
  FiSliders,
  FiBriefcase,
  FiMinusCircle,
  FiUser,
} from 'react-icons/fi';
import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  subWeeks,
  addDays,
} from 'date-fns';
import PropTypes from 'prop-types';
import ContentLoader from 'react-content-loader';
import CustomStore from 'devextreme/data/custom_store';
import { useControlContent } from '../../../hooks/controlContent';
import ProjectControlContentList from '../../ProjectControlContentList';
import SellValueTypeList from '../../../pages/Project/Tabs/SellValueType';
import TabPanel from '../../TabPanel';
import './styles.css';
import { errorMessage$ } from '../../../utils/subjects';
import NumberFormatValue from '../../NumberFormat';
import InputInlineEdit from '../../InputInlineEdit';
import SelectInlineEdit from '../../SelectInlineEdit';
import api from '../../../services/api';
import schedulingGroups from '../../../config/schedulingGroups';
import { useAuth } from '../../../hooks/auth';
import { ReactComponent as IdOneLogo } from '../../../assets/images/IDOne_Azul.svg';

const CustomContentLoaderComponent = ({ width }) => (
  <ContentLoader
    speed={2}
    width={width}
    height={14}
    viewBox={`0 0 ${width} 14`}
    backgroundColor="#f3f3f3"
    foregroundColor="#dedede"
  >
    <rect x="0" y="0" rx="7" ry="7" width={width} height="14" />
  </ContentLoader>
);
CustomContentLoaderComponent.propTypes = { width: PropTypes.number.isRequired };

export default function ProjectInfo({ onChange, projectId, type }) {
  const isSimulation = type === 'scheduling/simulation';
  const endpointPrefix = isSimulation ? 'scheduling/simulation' : 'scheduling';

  const { user } = useAuth();
  const [userScopes] = useState(() => (user && user.scopes) || []);
  const [allowEditScopes] = useState([
    schedulingGroups.Allocation,
    schedulingGroups.Admin,
  ]);
  const [allowEdit] = useState(() =>
    userScopes.some(scope => allowEditScopes.includes(scope)),
  );
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [projectValueInitial, setProjectValueInitial] = useState('');
  const [projectValueInitialDisplay, setProjectValueInitialDisplay] =
    useState('');
  const [currentMargin, setCurrentMargin] = useState('');
  const [marginSale, setMarginSale] = useState('');
  const [marginSaleDisplay, setMarginSaleDisplay] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [originalEndDate, setOriginalEndDate] = useState('');
  const [lastChargedExtensionEndDate, setLastChargedExtensionEndDate] =
    useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [client, setClient] = useState(null);
  const [clientName, setClientName] = useState('');
  const [businessUnit, setBusinessUnit] = useState(null);
  const [businessUnitName, setBusinessUnitName] = useState('');
  const [projectPartnerName, setProjectPartnerName] = useState('');
  const [projectManager, setProjectManager] = useState(null);
  const [projectManagerName, setProjectManagerName] = useState('');
  const [currency, setCurrency] = useState(null);
  const [currencyName, setCurrencyName] = useState('');
  const [currencyDisplayName, setCurrencyDisplayName] = useState('');
  const [comanagement, setComanagement] = useState(null);
  const [comanagementName, setComanagementName] = useState('');
  const [office, setOffice] = useState(null);
  const [officeName, setOfficeName] = useState('');
  const [billingOffice, setBillingOffice] = useState(null);
  const [billingOfficeName, setBillingOfficeName] = useState('');
  const [geographic, setGeographic] = useState(null);
  const [geographicName, setGeographicName] = useState('');
  const [cityState, setCityState] = useState('');
  const [projectValue, setProjectValue] = useState('');
  const [projectLocalValue, setProjectLocalValue] = useState('');
  const [firstAllocationMargin, setFirstAllocationMargin] = useState('');
  const [lastWeekMargin, setLastWeekMargin] = useState('');
  const [projectValueTotal, setProjectValueTotal] = useState('');
  const [projectValueTotalDisplay, setProjectValueTotalDisplay] = useState('');
  const [mainCurrency, setMainCurrency] = useState('BRL');
  const [mainCurrencyId, setMainCurrencyId] = useState('');
  const [missingDataInfo, setMissingDataInfo] = useState('');
  const [isLegacy, setIsLegacy] = useState(false);
  const [weeks, setWeeks] = useState([]);

  const [clientSource] = useState({
    store: new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        const response = await api.get('master/clients', {
          params: { where: { IsActive: true } },
        });
        return response.data;
      },
    }),
    paginate: true,
  });
  const [comanagementSource] = useState({
    store: new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        const { data } = await api.get('master/comanagements');
        return [
          { ID: null, Name: '(Empty)' },
          ...data.filter(
            x =>
              x.Name !== 'Brazil' && x.Name !== 'Latam' && x.Name !== 'Europe',
          ),
        ];
      },
    }),
    paginate: true,
  });
  const [professionalSource] = useState({
    store: new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        const response = await api.get('master/professionals', {
          params: { where: { IsActive: true } },
        });
        return response.data;
      },
    }),
    paginate: true,
  });
  const [businessUnitSource] = useState({
    store: new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        const { data } = await api.get('master/business-units');
        return data;
      },
    }),
    paginate: true,
  });
  const [currencySource] = useState({
    store: new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        const response = await api.get('master/currencies');
        return response.data.map(currencyProps => ({
          ...currencyProps,
          DisplayName: `${currencyProps.Name} - ${currencyProps.Abbreviation}`,
        }));
      },
    }),
    paginate: true,
  });
  const [officeSource] = useState({
    store: new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        const response = await api.get('master/offices');
        return response.data;
      },
    }),
    paginate: true,
  });
  const [geographicSource] = useState({
    store: new CustomStore({
      key: 'ID',
      loadMode: 'raw',
      load: async () => {
        const response = await api.get('master/geographics');
        return response.data;
      },
    }),
    paginate: true,
  });

  const { setControlContent, setPageTitle } = useControlContent();
  const history = useHistory();

  const [status, setStatus] = useState('');
  const [comanagementIdState, setComanagementIdState] = useState('');
  const [categories, setCategories] = useState([]);
  const [editedProject, setEditedProject] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const handleNavigation = useCallback(
    projectId => {
      history.push(`/projects/${projectId}/allocations`);
    },
    [history],
  );

  const onChangeInputValueInternal = useCallback(
    async data => {
      if (data.ProjectValueInitial) {
        setProjectValueInitial(data.ProjectValueInitial);
      }
      if (
        data.MarginSale &&
        userScopes &&
        !userScopes.includes(schedulingGroups.ReadAll)
      ) {
        setMarginSale(data.MarginSale);
      }
      if (onChange) onChange({ ID: Number(projectId), ...data });
      try {
        const project = await api.put(
          `${endpointPrefix}/projects/${projectId}`,
          data,
        );
        const margin = await api.get(
          `${endpointPrefix}/projects/${projectId}/margin`,
        );

        setProjectValueTotal(
          project.data.MainProjectValueTotal
            ? project.data.MainProjectValueTotal.toFixed(2)
            : '',
        );
      } catch (err) {
        console.log(err);
        errorMessage$.next({
          open: true,
          message: 'Error when updating project',
        });
      }
    },
    [onChange, projectId, userScopes, endpointPrefix],
  );

  const onChangeCurrencyInternal = useCallback(data => {
    if (data.Currency) {
      const parsedCurrency = data.Currency.split('-')[0].trim();
      setCurrencyName(parsedCurrency);
    }
  }, []);

  const loadProjectInfoInternal = useCallback(async () => {
    try {
      setLoading(true);
      const [project] = await Promise.all([
        api.get(`${endpointPrefix}/projects/${projectId}`),
      ]);
      if (isSimulation) {
        setName(project.data.ProjectName);
        setCode(project.data.ProjectCode);
        setClient(project.data.Client && project.data.Client.ID);
        setClientName(project.data.Client && project.data.Client.Name);
        setComanagement(
          project.data.Comanagement && project.data.Comanagement.ID,
        );
        setComanagementName(
          project.data.Comanagement && project.data.Comanagement.Name,
        );
        setOffice(project.data.Office && project.data.Office.ID);
        setOfficeName(project.data.Office && project.data.Office.Name);
        setProjectValueInitial(project.data.ProjectValueInitial);
        if (userScopes && !userScopes.includes(schedulingGroups.ReadAll)) {
          setCurrentMargin(project.data.MarginCurrent);
        }
      } else {
        setName(project.data.ProjectName);
        setCode(project.data.ProjectCode);
        setClient(project.data.Client && project.data.Client.ID);
        setClientName(project.data.Client && project.data.Client.Name);
        setBusinessUnit(
          project.data.BusinessUnit && project.data.BusinessUnit.ID,
        );
        setBusinessUnitName(
          project.data.BusinessUnit && project.data.BusinessUnit.Name,
        );
        setComanagement(
          project.data.Comanagement && project.data.Comanagement.ID,
        );
        setMainCurrencyId(project.data.IDMainCurrency);
        setIsLegacy(project.data.IsLegacy);
        setMissingDataInfo(project.data.MissingFinancialDataInfo);
        setComanagementName(
          project.data.Comanagement && project.data.Comanagement.Name,
        );
        setOffice(project.data.Office.ID);
        setBillingOffice(
          project.data.BillingOffice && project.data.BillingOffice.ID,
        );
        setBillingOfficeName(
          project.data.BillingOffice && project.data.BillingOffice.Name,
        );
        setOfficeName(project.data.Office.Name);
        setGeographic(project.data.Geographic.ID);
        setGeographicName(project.data.Geographic.Name);
        setCityState(project.data.City);
        setWeeks(
          project.data.ProjectScheduleWeeks.map(x => ({
            ID: x.ScheduleWeek.WeekStartDate,
            Name: format(
              startOfWeek(addDays(parseISO(x.ScheduleWeek.WeekStartDate), 2), {
                weekStartsOn: 1,
              }),
              'dd MMM yyyy',
            ),
          })),
        );
        if (userScopes && !userScopes.includes(schedulingGroups.ReadAll)) {
          setCurrentMargin(project.data.MarginCurrent);
        }
        setProjectValueInitial(project.data.ProjectValueInitial);
        setProjectValueTotal(project.data.MainProjectValueTotal);
        setMainCurrency(project.data.MainCurrency?.Name || 'BRL');
        setFirstAllocationMargin(project.data.MarginFirstAllocation);
        setProjectPartnerName(project.data.ProfessionalPartner.Name);
        setProjectManager(project.data.ProfessionalManager.ID);
        setProjectManagerName(project.data.ProfessionalManager.Name);
        setCurrency(project.data.Currency.ID);
        setCurrencyName(project.data.Currency.Name);
        setCurrencyDisplayName(
          `${project.data.Currency.Name} - ${project.data.Currency.Abbreviation}`,
        );
        setMarginSale(project.data.MarginSale || '0');

        setStartDate(project.data.StartDateReal || project.data.StartDate);
        setEndDate(
          project.data.EndDate &&
            format(
              endOfWeek(subWeeks(parseISO(project.data.EndDate), 1), {
                weekStartsOn: 6,
              }),
              'dd MMM yyyy',
            ),
        );
        setOriginalEndDate(
          project.data.OriginalEndDate &&
            format(
              endOfWeek(subWeeks(parseISO(project.data.OriginalEndDate), 1), {
                weekStartsOn: 6,
              }),
              'dd MMM yyyy',
            ),
        );
        setLastChargedExtensionEndDate(
          project.data.ChargedExtensionDate &&
            format(
              endOfWeek(
                subWeeks(parseISO(project.data.ChargedExtensionDate), 1),
                { weekStartsOn: 6 },
              ),
              'dd MMM yyyy',
            ),
        );
        setProjectLocalValue(
          project.data.MainProjectValueTotalLocal
            ? new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: project.data?.Currency?.Name || 'BRL',
              }).format(project.data.MainProjectValueTotalLocal || 0)
            : 'Missing financial data',
        );
        setProjectValue(
          new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: project.data?.MainCurrency?.Name || 'BRL',
          }).format(project.data.MainProjectValueTotal),
        );
        setLastWeekMargin(project.data.MarginLastWeek);
      }
      setLoading(false);
    } catch (err) {
      errorMessage$.next({
        open: true,
        message: `Error loading project information. (${err})`,
      });
    }
  }, [projectId, userScopes, endpointPrefix, type, isSimulation]);

  useEffect(() => {
    loadProjectInfoInternal();
  }, [loadProjectInfoInternal]);

  useEffect(() => {
    try {
      setProjectValueTotalDisplay(
        new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: mainCurrency,
        }).format(projectValueTotal),
      );
      setProjectValueInitialDisplay(
        new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: currencyName,
        }).format(projectValueInitial),
      );
    } catch (err) {
      setProjectValueTotalDisplay(
        new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: mainCurrency,
        }).format(projectValueTotal),
      );
      setProjectValueInitialDisplay(
        new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: mainCurrency,
        }).format(projectValueInitial),
      );
    }
  }, [
    currencyName,
    projectValueInitial,
    projectValueTotal,
    mainCurrency,
  ]);

  useEffect(() => {
    setMarginSaleDisplay(`${marginSale}%`);
  }, [marginSale]);

  useEffect(() => {
    setLoading(true);
    setTabValue(0);
    Promise.all([
      api.get(`${endpointPrefix}/projects/${projectId}`),
      api.get(`scheduling/sell-value-types-categories`),
    ])
      .then(([project, parsedCategories]) => {
        setName(project.data.ProjectName);
        setCode(project.data.ProjectCode);
        setStatus(project.data.ProjectStatus.Description);
        setComanagementIdState(project.data.Comanagement.ID);

        // Filter to active categories only
        const activeCats = parsedCategories.data.filter(c => c.IsActive);
        setCategories(activeCats);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        errorMessage$.next({
          open: true,
          message: `Error on load project info ${err}`,
        });
      });
  }, [projectId, endpointPrefix]);

  useEffect(() => {
    setControlContent(
      <ProjectControlContentList
        onSelectProject={handleNavigation}
        editedProject={editedProject}
      />,
    );
    setPageTitle('Projects');
  }, [editedProject, handleNavigation, setControlContent, setPageTitle]);

  return (
    <>
      <Tabs
        value={tab}
        onChange={(event, newValue) => setTab(newValue)}
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab
          icon={
            <Tooltip title="Info" arrow placement="top">
              <span style={{ display: 'flex' }}><FiInfo size={20} /></span>
            </Tooltip>
          }
        />
        {categories.map(category => {
          let icon;
          switch (category.Description) {
            case 'Expenses':
              icon = <FiDollarSign size={20} />;
              break;
            case 'ID One':
              icon = <IdOneLogo className="idone-logo" />;
              break;
            case 'Value adjustments':
              icon = <FiSliders size={20} />;
              break;
            case 'External Professional Cost':
              icon = <FiBriefcase size={20} />;
              break;
            case 'Hogan':
              icon = <FiUser size={20}/>
              break
            case 'Non-Chargeable':
              icon = <FiMinusCircle size={20} />;
              break;
            default:
              icon = <FiMinusCircle size={20} />;
              break;
          }
          return (
            <Tab
              key={`category-${category.ID}`}
              value={category.ID}
              icon={
                <Tooltip title={category.Description} arrow placement="top">
                  <span style={{ display: 'flex' }}>{icon}</span>
                </Tooltip>
              }
            />
          );
        })}
      </Tabs>
      <TabPanel value={tab} index={0} style={{ padding: 0 }}>
        {isSimulation ? (
          <>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <InputInlineEdit
                  value={name}
                  dataField="ProjectName"
                  onChange={onChangeInputValueInternal}
                  caption="Project Name"
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Project Name: </span>
                  <span>{name}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <InputInlineEdit
                  value={code}
                  dataField="ProjectCode"
                  onChange={onChangeInputValueInternal}
                  caption="Project Code"
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Project Code: </span>
                  <span>{code}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={client}
                  displayValue={clientName}
                  dataField="Client"
                  caption="Client"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={clientSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Client: </span>
                  <span>{clientName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={comanagement}
                  displayValue={comanagementName}
                  dataField="Comanagement"
                  caption="Practice"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={comanagementSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Practice: </span>
                  <span>{comanagementName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={office}
                  displayValue={officeName}
                  dataField="Office"
                  caption="Office"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={officeSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Office: </span>
                  <span>{officeName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <InputInlineEdit
                  value={projectValueInitial}
                  displayValue={projectValueInitialDisplay}
                  dataField="ProjectValueInitial"
                  caption="Project Initial Value"
                  onChange={onChangeInputValueInternal}
                  inputProps={{ inputComponent: NumberFormatValue }}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">
                    Project Initial Value:{' '}
                  </span>
                  <span>{projectValueInitialDisplay}</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="big-numbers">
              <div className="project-info-box numbers">
                <h3>Value</h3>
                {loading ? (
                  <CustomContentLoaderComponent width={140} />
                ) : (
                  <strong>{projectValue}</strong>
                )}
              </div>
              <div className="project-info-box numbers">
                <h3>Local Value</h3>
                {loading ? (
                  <CustomContentLoaderComponent width={140} />
                ) : (
                  <strong>{projectLocalValue}</strong>
                )}
              </div>
              <div className="project-info-box numbers">
                <h3>
                  1<sup>st</sup> Allocation
                </h3>
                <strong>
                  {mainCurrencyId === 2 && isLegacy ? (
                    'N/A'
                  ) : loading ? (
                    <CustomContentLoaderComponent width={140} />
                  ) : (
                    `${firstAllocationMargin || 0}%`
                  )}
                </strong>
              </div>
              <div className="project-info-box numbers">
                <h3>Margin</h3>
                <strong>
                  {!missingDataInfo ? (
                    loading ? (
                      <CustomContentLoaderComponent width={140} />
                    ) : isNaN(parseFloat(currentMargin)) ? (
                      '0.00%'
                    ) : (
                      `${parseFloat(currentMargin).toFixed(2)}%`
                    )
                  ) : (
                    '-'
                  )}
                </strong>
              </div>
              <div className="project-info-box numbers">
                <h3>Last Week</h3>
                <strong>
                  {mainCurrencyId === 2 && isLegacy ? (
                    'N/A'
                  ) : loading ? (
                    <CustomContentLoaderComponent width={140} />
                  ) : (
                    `${lastWeekMargin || 0}%`
                  )}
                </strong>
              </div>
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <InputInlineEdit
                  value={name}
                  dataField="ProjectName"
                  onChange={onChangeInputValueInternal}
                  caption="Project Name"
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Project Name: </span>
                  <span>{name}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <InputInlineEdit
                  value={code}
                  dataField="ProjectCode"
                  onChange={onChangeInputValueInternal}
                  caption="Project Code"
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Project Code: </span>
                  <span>{code}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={client}
                  displayValue={clientName}
                  dataField="Client"
                  caption="Client"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={clientSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Client: </span>
                  <span>{clientName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={businessUnit}
                  displayValue={businessUnitName}
                  dataField="BusinessUnit"
                  caption="Business Unit"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={businessUnitSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Business Unit: </span>
                  <span>{businessUnitName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={comanagement}
                  displayValue={comanagementName}
                  dataField="Comanagement"
                  caption="Practice"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={comanagementSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Practice: </span>
                  <span>{comanagementName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={projectManager}
                  displayValue={projectManagerName}
                  dataField="ProfessionalManager"
                  caption="Manager"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={professionalSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Manager: </span>
                  <span>{projectManagerName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={projectManager}
                  displayValue={projectPartnerName}
                  dataField="ProfessionalPartner"
                  caption="Partner"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={professionalSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Partner: </span>
                  <span>{projectPartnerName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={office}
                  displayValue={officeName}
                  dataField="Office"
                  caption="Office"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={officeSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Office: </span>
                  <span>{officeName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={billingOffice}
                  displayValue={billingOfficeName}
                  dataField="BillingOffice"
                  caption="Billing Office"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={officeSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Billing Office: </span>
                  <span>{billingOfficeName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={geographic}
                  displayValue={geographicName}
                  dataField="Geographic"
                  caption="Geographic"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={geographicSource}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Geographic: </span>
                  <span>{geographicName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <InputInlineEdit
                  value={cityState}
                  dataField="City"
                  caption="City/State"
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">City/State: </span>
                  <span>{cityState}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={currency}
                  displayValue={currencyDisplayName}
                  dataField="Currency"
                  caption="Currency"
                  displayExpr="DisplayName"
                  valueExpr="ID"
                  dataSource={currencySource}
                  onChange={onChangeInputValueInternal}
                  onChangeDisplayName={onChangeCurrencyInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Currency: </span>
                  <span>{currencyDisplayName}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <SelectInlineEdit
                  value={startDate}
                  displayValue={
                    startDate &&
                    format(
                      startOfWeek(addDays(parseISO(startDate), 2), {
                        weekStartsOn: 1,
                      }),
                      'dd MMM yyyy',
                    )
                  }
                  dataField="StartDateReal"
                  caption="Start Date"
                  displayExpr="Name"
                  valueExpr="ID"
                  dataSource={weeks}
                  onChange={onChangeInputValueInternal}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Start Date: </span>
                  {loading ? (
                    <CustomContentLoaderComponent width={140} />
                  ) : (
                    <span>
                      {startDate &&
                        format(
                          startOfWeek(addDays(parseISO(startDate), 2), {
                            weekStartsOn: 1,
                          }),
                          'dd MMM yyyy',
                        )}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">End Date: </span>
                  <span>{endDate}</span>
                </div>
              )}
            </div>
            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <InputInlineEdit
                  value={marginSale}
                  displayValue={marginSaleDisplay}
                  dataField="MarginSale"
                  caption="Margin Sale"
                  onChange={onChangeInputValueInternal}
                  inputProps={{ inputComponent: NumberFormatValue }}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Margin Sale: </span>
                  <span>{marginSaleDisplay}</span>
                </div>
              )}
            </div>

            <div className="project-info-box">
              {loading ? (
                <CustomContentLoaderComponent width={140} />
              ) : allowEdit ? (
                <InputInlineEdit
                  value={projectValueInitial}
                  displayValue={projectValueInitialDisplay}
                  dataField="ProjectValueInitial"
                  caption="Project Initial Value"
                  onChange={onChangeInputValueInternal}
                  inputProps={{
                    inputComponent: NumberFormatValue,
                  }}
                />
              ) : (
                <div className="list-item-content">
                  <span className="secondary-text">Project Initial Value:</span>
                  {loading ? (
                    <CustomContentLoaderComponent width={140} />
                  ) : (
                    <span>{projectValueInitialDisplay}</span>
                  )}
                </div>
              )}
            </div>

            <div className="project-info-box">
              <div className="list-item-content">
                {loading ? (
                  <CustomContentLoaderComponent width={140} />
                ) : (
                  <>
                    <span className="secondary-text">Original End Date: </span>
                    <span>{originalEndDate}</span>
                  </>
                )}
              </div>
            </div>
            <div className="project-info-box">
              <div className="list-item-content">
                {loading ? (
                  <CustomContentLoaderComponent width={140} />
                ) : (
                  <>
                    <span className="secondary-text">
                      Last Charged Extension End Date:{' '}
                    </span>
                    <span>{lastChargedExtensionEndDate}</span>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </TabPanel>
      {categories.map(category => (
        <div
          key={`category-${category.ID}`}
          role="tabpanel"
          hidden={tab !== category.ID}
          style={{ padding: 24 }}
        >
          <SellValueTypeList
            categoryId={category.ID}
            projectId={projectId}
            projectStatus={status}
            comanagementId={comanagementIdState}
            isSimulation={isSimulation}
          />
        </div>
      ))}
    </>
  );
}

ProjectInfo.propTypes = {
  onChange: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  type: PropTypes.string,
};
